import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { Product } from '../../types.ts';
import {
  X,
  MessageCircle,
  CheckCircle2,
  Tag,
  ChevronLeft,
  ChevronRight,
  Play,
  Film,
  Image as ImageIcon,
  ShieldCheck,
  Truck,
  PhoneCall,
} from 'lucide-react';
import {
  isIframeEmbed,
  extractIframeSrc,
  getYouTubeId,
  getYouTubeEmbedUrl,
} from '../../utils/videoUtils.ts';

export default function ProductDetailModal() {
  const { selectedProduct, setSelectedProduct, settings, navigate } = useApp();
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);

  // Mobile Touch Swipe support
  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);

  const primaryColor = settings?.theme.primaryColor || '#059669';

  // Construct combined media list: photos + optional video
  interface MediaItem {
    type: 'image' | 'video';
    url: string;
    videoType?: 'file' | 'youtube' | 'url';
    thumb?: string;
  }

  const mediaItems: MediaItem[] = [];

  if (selectedProduct) {
    // 1. Photos (Cover + Gallery)
    const photos = selectedProduct.gallery && selectedProduct.gallery.length > 0
      ? selectedProduct.gallery
      : [selectedProduct.image || 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=800&q=80'];

    photos.forEach((photoUrl) => {
      if (photoUrl) {
        mediaItems.push({
          type: 'image',
          url: photoUrl,
          thumb: photoUrl,
        });
      }
    });

    // 2. Video if exists
    if (selectedProduct.videoUrl) {
      mediaItems.push({
        type: 'video',
        url: selectedProduct.videoUrl,
        videoType: selectedProduct.videoType || (selectedProduct.videoUrl.includes('youtube.com') || selectedProduct.videoUrl.includes('youtu.be') ? 'youtube' : 'file'),
        thumb: selectedProduct.image || '',
      });
    }
  }

  const handlePrev = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (mediaItems.length <= 1) return;
    setActiveMediaIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
  };

  const handleNext = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (mediaItems.length <= 1) return;
    setActiveMediaIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedProduct(null);
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };
    if (selectedProduct) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedProduct, mediaItems.length]);

  if (!selectedProduct) return null;

  const currentMedia = mediaItems[activeMediaIndex] || mediaItems[0];

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    const deltaY = e.changedTouches[0].clientY - touchStartYRef.current;
    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  const formatRupiah = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(val);
  };

  const waNumber = (settings?.contact?.whatsappNumber || '6281234567890').replace(/[^0-9]/g, '');
  const priceInfo = selectedProduct.hidePrice ? '' : ` (${formatRupiah(selectedProduct.price)})`;
  const defaultMsg = `Halo Admin Nusantara, saya tertarik dengan "${selectedProduct.title}"${priceInfo}. Mohon informasi ketersediaan tanggal/stok dan cara pemesanan resminya.`;
  const waMessage = selectedProduct.whatsappMessage || defaultMsg;
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;

  const getYoutubeEmbed = (url: string) => {
    if (!url) return '';
    if (url.includes('embed/')) return url;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
    return match ? `https://www.youtube-nocookie.com/embed/${match[1]}?autoplay=1` : url;
  };

  return (
    <div
      onClick={() => setSelectedProduct(null)}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-3 sm:p-4 md:p-6 animate-in fade-in"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl overflow-hidden max-w-4xl w-full shadow-2xl border border-slate-200 max-h-[92vh] flex flex-col animate-in zoom-in-95 duration-150"
      >
        {/* Modal Header */}
        <div className="px-4 sm:px-5 py-3.5 sm:py-4 flex items-center justify-between border-b border-slate-100 bg-slate-50/90 gap-3 shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
            <span
              className="px-2.5 py-0.5 rounded-md text-[11px] font-extrabold uppercase tracking-wide text-white shrink-0"
              style={{ backgroundColor: primaryColor }}
            >
              {selectedProduct.category}
            </span>
            {selectedProduct.badge && (
              <span className="bg-amber-100 text-amber-800 text-[11px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1 shrink-0">
                <Tag className="w-3 h-3 text-amber-600" />
                {selectedProduct.badge}
              </span>
            )}
            {selectedProduct.videoUrl && (
              <span className="bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1 shrink-0">
                <Film className="w-3 h-3 text-purple-600" />
                Video Included
              </span>
            )}
          </div>
          <button
            onClick={() => setSelectedProduct(null)}
            aria-label="Close Details"
            className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-800 rounded-xl hover:bg-slate-200 transition-colors shrink-0 cursor-pointer"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            {/* Left Column: Interactive Media Slider */}
            <div className="md:col-span-7 flex flex-col gap-3">
              <div
                className="relative aspect-4/3 rounded-2xl overflow-hidden bg-slate-950 border border-slate-200 shadow-inner group select-none touch-pan-y"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                {currentMedia?.type === 'video' ? (
                  isIframeEmbed(currentMedia.url) ? (
                    <iframe
                      src={extractIframeSrc(currentMedia.url)}
                      title={selectedProduct.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : currentMedia.videoType === 'youtube' || getYouTubeId(currentMedia.url) ? (
                    <iframe
                      src={getYouTubeEmbedUrl(currentMedia.url, true)}
                      title={selectedProduct.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={currentMedia.url}
                      controls
                      autoPlay
                      className="w-full h-full object-contain"
                    />
                  )
                ) : (
                  <img
                    src={currentMedia?.url || selectedProduct.image}
                    alt={selectedProduct.title}
                    className="w-full h-full object-cover select-none transition-all duration-300"
                  />
                )}

                {/* Slider Controls (Next / Prev) */}
                {mediaItems.length > 1 && (
                  <>
                    <button
                      onClick={handlePrev}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-xs transition-transform hover:scale-105 opacity-80 group-hover:opacity-100 z-10"
                      aria-label="Previous slide"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleNext}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-xs transition-transform hover:scale-105 opacity-80 group-hover:opacity-100 z-10"
                      aria-label="Next slide"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>

                    <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 bg-black/65 text-white text-[11px] font-semibold px-2.5 py-0.5 rounded-full backdrop-blur-xs">
                      {activeMediaIndex + 1} / {mediaItems.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails Row */}
              {mediaItems.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
                  {mediaItems.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveMediaIndex(idx)}
                      className={`relative shrink-0 w-16 sm:w-20 h-12 sm:h-14 rounded-xl overflow-hidden border-2 transition-all ${
                        activeMediaIndex === idx
                          ? 'border-emerald-600 ring-2 ring-emerald-500/30 scale-102'
                          : 'border-slate-200 opacity-60 hover:opacity-100'
                      }`}
                    >
                      {item.type === 'video' ? (
                        <div className="w-full h-full bg-slate-900 flex items-center justify-center text-white relative">
                          {item.thumb ? (
                            <img src={item.thumb} alt="video thumb" className="w-full h-full object-cover opacity-60" />
                          ) : null}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                            <Play className="w-4 h-4 text-white fill-white" />
                          </div>
                        </div>
                      ) : (
                        <img src={item.url} alt={`thumb-${idx}`} className="w-full h-full object-cover" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column: Product Information & Soft-Selling Booking CTA */}
            <div className="md:col-span-5 flex flex-col justify-between h-full space-y-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 leading-tight mb-2">
                  {selectedProduct.title}
                </h2>

                <div className="mb-4 flex items-baseline gap-2">
                  {selectedProduct.hidePrice ? (
                    <span
                      className="text-xl sm:text-2xl font-extrabold tracking-tight"
                      style={{ color: primaryColor }}
                    >
                      {selectedProduct.priceCustomLabel || 'Contact Advisory / Inquire'}
                    </span>
                  ) : (
                    <>
                      <span
                        className="text-2xl sm:text-3xl font-extrabold tracking-tight"
                        style={{ color: primaryColor }}
                      >
                        {formatRupiah(selectedProduct.price)}
                      </span>
                      {selectedProduct.originalPrice && selectedProduct.originalPrice > selectedProduct.price && (
                        <span className="text-xs sm:text-sm text-slate-400 line-through">
                          {formatRupiah(selectedProduct.originalPrice)}
                        </span>
                      )}
                    </>
                  )}
                </div>

                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-4 whitespace-pre-line">
                  {selectedProduct.description}
                </p>

                {/* Features List */}
                {selectedProduct.features && selectedProduct.features.length > 0 && (
                  <div className="space-y-2 mb-4 bg-slate-50 p-3.5 rounded-2xl border border-slate-100">
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider block">
                      Scope & Key Deliverables:
                    </span>
                    {selectedProduct.features.map((feat, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Direct WhatsApp CTA Button & Dedicated Page Link */}
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3.5 rounded-xl text-white font-bold text-xs sm:text-sm shadow-md hover:opacity-95 transition-transform hover:scale-[1.01] flex items-center justify-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                >
                  <MessageCircle className="w-4 h-4 fill-white" />
                  <span>Inquire / Consult via WhatsApp</span>
                </a>

                <button
                  type="button"
                  onClick={() => {
                    const slug = selectedProduct.slug || selectedProduct.id;
                    setSelectedProduct(null);
                    navigate('product-detail', slug);
                  }}
                  className="w-full py-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
                >
                  <span>Open Dedicated Page & Share URL</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>

                <p className="text-[10px] text-center text-slate-400 mt-1">
                  Prompt advisory response &bull; Tailored proposal negotiation &bull; Secure compliance
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
